// The first thing a brand-new account sees: START WITH THESE, or START WITH
// NOTHING.
//
// The approved design (docs/design/prototype-v1.md section 5, row 6, and
// `screenshots/phone-firstrun.png`), ported declaration for declaration from the
// prototype's `firstrun()` rather than reinvented: the greeting, one paragraph
// saying what the examples are, a card previewing them, the two actions, and one
// line of fine print promising there is nothing else to do.
//
// IT IS A GATE, NOT A SCREEN, and that is the whole of its shape. It is not in
// `shell/screens.ts`, it has no path, and no tab or control reaches it: it
// REPLACES the shell while `useDayScreen`'s `firstRun` is true, exactly as
// `SignedOut` replaces it while `signedOut` is true. A `/first-run` path would
// be one anybody could visit, and there is no honest thing to show somebody who
// has already answered - the question is not a place, it is a state of the
// account, and the account answers it once.
//
// WHAT IS ON IT COMES FROM THE SERVER. The three schedules, their hues, their
// icons and the items inside them are `firstRunContent` in
// api/src/domain/first-run.ts, sent as `examples` - because this screen offers
// to WRITE them, and a list written down here would go on promising Work, School
// and Gym after the seed said something else. It is the rule that makes
// `schedules.cap`, `meta` and the board's column labels travel, applied to the
// one screen where being wrong means describing content that does not appear.
//
// TWO THINGS FROM THE PROTOTYPE ARE DELIBERATELY NOT HERE:
//
//   - Its fourth preview row, "Reminders - one-off things, in no schedule". The
//     seed writes no reminders, so the row would promise something pressing the
//     button does not do. It comes back from the server the day the seed writes
//     one, with nothing here edited.
//   - The half-sentence "and a short reminders list" in its opening paragraph,
//     for the same reason.

import type { CSSProperties } from 'react';
import { Icon } from '../day/Icon.tsx';
import { hueIn } from '../edit/edit-model.ts';
import { useMode } from '../shell/theme.ts';
import { useVoice, wording } from '../shell/voice.ts';
import type { FirstRunState } from './useFirstRun.ts';

export interface FirstRunScreenProps {
  state: FirstRunState;
  /**
   * Why the DAY behind this gate did not arrive, or null.
   *
   * It can only be non-null after the choice has been made: the gate is raised
   * by a day payload that landed, and the only re-read while it is up is the one
   * the choice asks for. That re-read is what takes the gate down, so a failure
   * there leaves an answered account looking at two dead buttons with nothing on
   * screen saying why - the same dead end the signed-out gate was built to end,
   * one screen along. This is how it says so, and `onRetryDay` is the way out.
   */
  dayProblem: string | null;
  /** Ask for the day again. `useDayScreen`'s own retry, not a second one. */
  onRetryDay: () => void;
}

/**
 * What this gate is showing, as ONE value rather than four nested conditions.
 *
 * The order is the order the states outrank each other, and each one is drawn
 * to the exclusion of the rest: a person answering does not also need the
 * paragraph explaining what the examples are, and somebody whose account is
 * already made must not be offered the choice again.
 */
type Stage = 'day-lost' | 'unreadable' | 'reading' | 'asking';

function stageOf(state: FirstRunState, dayProblem: string | null): Stage {
  // The answer landed and the day behind it did not. Nothing is left to choose,
  // so nothing about the choice is drawn.
  if (dayProblem !== null) return 'day-lost';
  if (state.problem !== null) return 'unreadable';
  if (state.examples === null) return 'reading';
  return 'asking';
}

export function FirstRunScreen({ state, dayProblem, onRetryDay }: FirstRunScreenProps) {
  const { examples, name, busy, failure, retry, choose } = state;
  const stage = stageOf(state, dayProblem);
  // `startAppearance` has already resolved the mode by the time this renders, so
  // the very first paint a new account ever sees is the mode-correct hue rather
  // than a light hex on a dark page (`edit/edit-model.ts`).
  const mode = useMode();
  // The greeting is the one voiced line on this gate. The two choices, the
  // schedule names under them and every failure line below stay plain: they are
  // what this screen is asking and what it reports when a press does not land.
  const voice = useVoice();

  return (
    <div className="app">
      <div className="gate">
        <div className="gate-in">
          {/* The name is the server's - the first word of the Google display
              name, computed there so two clients cannot greet one person two
              ways. An account with no name at all gets the bare greeting rather
              than "Welcome, ." (`greetingName` in api/src/domain/profile.ts).
              It is the one line every stage below shares. */}
          <h1>
            {name === ''
              ? wording('firstrun.titleAnon', voice)
              : wording('firstrun.title', voice, { name })}
          </h1>

          {stage === 'day-lost' && (
            <>
              {/* ANNOUNCED, because it ARRIVES: it replaces the choice after a
                  press, on a gate that stays mounted around it. SignedOut.tsx's
                  "an ordinary paragraph, and NOT a live region" note is about
                  text that is on screen at mount and read with everything else -
                  which is precisely not this. It is the same reason the shell's
                  toast is mounted empty and carries `role="status"`. */}
              <p className="gatenotice" role="alert">
                Your account is ready. Perfect Note could not load your day. {dayProblem}
              </p>
              <button className="btn primary big" onClick={onRetryDay}>
                Try again
              </button>
            </>
          )}

          {/* The preview could not be read. The two answers are NOT drawn under
              this: "Start with these" with no "these" on screen is a button
              whose whole meaning is missing, and it is the one press in this
              product that cannot be taken back. Trying again is offered because
              the failure is a server that was not there, and those get better -
              a 401 never reaches here, it raises the signed-out gate instead. */}
          {stage === 'unreadable' && (
            <>
              <p className="gatenotice">{state.problem}</p>
              <button className="btn primary big" onClick={retry}>
                Try again
              </button>
            </>
          )}

          {/* The PREVIEW is being read - nothing has been chosen and nothing is
              being written. Worded away from the in-flight line below on
              purpose: two moments a person can tell apart must not be described
              in two sentences they cannot. */}
          {stage === 'reading' && <p className="gatenotice">Getting the examples ready…</p>}

          {stage === 'asking' && examples !== null && (
            <>
              <p className="tag seedlead">
                Perfect Note starts with three example schedules, so you can see how it works.
                Rename them, recolour them, or delete them. Nothing here is fixed.
              </p>

              <ul className="seedcard">
                {examples.map((example) => (
                  <li
                    className="seedrow"
                    key={example.name}
                    // The hue arrives as `#RRGGBB` and is set as `--c` on the
                    // row; the chip's tint is derived from it in CSS, exactly as
                    // it is on a card on the day. One declaration, right on
                    // every surface and in every theme (web/src/README.md) -
                    // through `hueIn`, because the stored hex is the light one.
                    style={{ '--c': hueIn(example.colorHex, mode) } as CSSProperties}
                  >
                    <span className="chip">
                      <Icon name={example.symbol} />
                    </span>
                    <span className="t">
                      <b>{example.name}</b>
                      <span>{example.summary}</span>
                    </span>
                    {/* A ticked circle in the schedule's own hue: the thing this
                        card is a picture OF is a day with something on it. Drawn
                        by CSS and marked `aria-hidden`, because it says nothing
                        a reader needs - the row above it already did. */}
                    <span className="mini" aria-hidden="true" />
                  </li>
                ))}
              </ul>

              {/* Beside the buttons rather than on the toast: the toast belongs
                  to the shell, and the shell is not mounted behind this gate.
                  `role="alert"` for the same reason the day-lost line above has
                  one - it arrives after a press, and the only signal that the
                  one irreversible press in this product did not land must not be
                  text nobody is told is there. */}
              {failure !== null && (
                <p className="gatenotice" role="alert">
                  {failure}
                </p>
              )}

              {/* THE ANSWER IS OUT AND THE DAY IS NOT BACK YET. `choose` never
                  lowers the hold on success (`useFirstRun`), because the gate
                  comes down on the day rather than on the write - so without
                  this line the wait is two greyed-out buttons and no words, for
                  a write of three schedules and then a whole day read. The
                  prototype never modelled this moment; the words are the
                  client's own `sheetwait`, announced exactly as the failure
                  beside it is. One sentence for both answers, because after
                  either one it is the day that is being fetched. */}
              {busy && failure === null && (
                <p className="sheetwait" role="status">
                  Setting up your day…
                </p>
              )}

              <button className="btn primary big" disabled={busy} onClick={() => choose(true)}>
                Start with these
              </button>
              <button className="btn big gate-second" disabled={busy} onClick={() => choose(false)}>
                Start with nothing
              </button>

              <p className="finetext">
                There is nothing else to set up, and no tutorial to sit through.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
